// auth/authGuard.js
// Fixes the "302 / CORS on aad" failure that hits every Azure Static Web App
// after the AAD session silently expires (~8–24h).
//
// Symptom: once the token lapses, SWA answers protected calls with a 302 to the
// shared identity host (identity.<n>.azurestaticapps.net/.redirect/aad?…). A
// full-page navigation can follow that, but a `fetch`/XHR CANNOT follow a
// cross-origin auth redirect — the browser blocks it as a CORS error (initiator
// = aad), so every button/refresh silently fails until a hard reload.
//
// Fix: wrap window.fetch once. For same-origin /api/* and /.auth/* calls we set
// redirect:'manual' so an auth 302 surfaces as an opaqueredirect (status 0)
// instead of a CORS explosion; when we see that (or a 401, or a network error
// that /.auth/me confirms is an expired session) we do a FULL-PAGE redirect to
// /.auth/login/aad, which the browser is allowed to follow through AAD and back.
//
// Portable: copy this file into any SWA and call installAuthGuard() once at
// startup (before the app renders). No per-call-site changes needed.

let installed = false;

function isAuthGuardedUrl(url) {
  try {
    const u = new URL(url, window.location.origin);
    if (u.origin !== window.location.origin) return false;   // only our own origin
    return u.pathname.startsWith('/api/') || u.pathname.startsWith('/.auth/');
  } catch {
    return false;
  }
}

function redirectToLogin() {
  // Start the login on OUR origin so the browser can follow the cross-origin
  // AAD hops as a top-level navigation, then return to where the user was.
  const back = window.location.pathname + window.location.search + window.location.hash;
  window.location.assign(`/.auth/login/aad?post_login_redirect_uri=${encodeURIComponent(back)}`);
  // The page is navigating away — never resolve so callers don't act on a
  // half-authenticated state.
  return new Promise(() => {});
}

export function installAuthGuard() {
  if (installed || typeof window === 'undefined' || !window.fetch) return;
  installed = true;
  const originalFetch = window.fetch.bind(window);

  // Confirm the session is actually gone before nuking the page — avoids
  // redirect loops on a genuine transient network blip. /.auth/me returns 200
  // with { clientPrincipal: null } when unauthenticated (it never 302s).
  async function sessionExpired() {
    try {
      const res = await originalFetch('/.auth/me', { headers: { accept: 'application/json' } });
      if (!res.ok) return false;
      const body = await res.json();
      return !body || !body.clientPrincipal;
    } catch {
      return false;   // can't tell → treat as a real network error, don't redirect
    }
  }

  window.fetch = async (input, init = {}) => {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    if (!isAuthGuardedUrl(url)) return originalFetch(input, init);

    let res;
    try {
      res = await originalFetch(input, { ...init, redirect: 'manual' });
    } catch (err) {
      // A cross-origin auth redirect that got blocked shows up here as a
      // TypeError. Only re-auth if the session is confirmed gone.
      if (await sessionExpired()) return redirectToLogin();
      throw err;
    }

    // opaqueredirect (status 0) = the SWA auth 302 we intercepted; 401 = SWA
    // rejected an expired/absent principal. Either way, re-auth.
    if (res.type === 'opaqueredirect' || res.status === 401) {
      return redirectToLogin();
    }
    return res;
  };
}
